import { useEffect, useState, useCallback, useRef } from 'react';
import { getMobilePipeline } from '../../lib/api';
import { MagnifyingGlass, X, CaretRight, Fire, Clock, Warning } from '@phosphor-icons/react';
import MobileLoanDetail from './MobileLoanDetail';

const FILTERS = [
  { id: 'all',             label: 'All active' },
  { id: 'needs-attention', label: 'Needs attention' },
  { id: 'closing-soon',    label: 'Closing <7d' },
  { id: 'overdue',         label: 'Overdue' },
];

export default function MobilePipeline({ user, initialFilter = 'all' }) {
  const [filter, setFilter] = useState(initialFilter);
  const [scope, setScope] = useState('team'); // 'team' | 'me'
  const [q, setQ] = useState('');
  const [data, setData] = useState({ rows: [], total: 0, shown: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFile, setOpenFile] = useState(null);
  const scrollPos = useRef(0);

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await getMobilePipeline({ filter, scope, q, user_initials: user?.initials || '', limit: 100 });
      setData(d);
    } catch (e) { setError('Could not load pipeline'); }
    finally { setLoading(false); }
  }, [filter, scope, q, user]);

  useEffect(() => { load(); }, [load]);

  // Preserve window scroll position when returning from a loan detail overlay
  useEffect(() => {
    if (!openFile && scrollPos.current) {
      window.scrollTo(0, scrollPos.current);
    }
  }, [openFile]);

  const handleOpen = (id) => {
    scrollPos.current = window.scrollY;
    setOpenFile(id);
  };

  return (
    <div className="pb-4" data-testid="mobile-pipeline">
      {/* Sticky header: search + scope + filters */}
      <div className="sticky top-14 z-20 bg-background pt-2 pb-1 -mx-4 px-4 border-b border-border">
        <div className="relative mb-2">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} inputMode="search" enterKeyHint="search" autoComplete="off"
            data-testid="pipeline-search" placeholder="Search borrowers…"
            style={{ fontSize: '16px' }}
            className="w-full h-10 pl-8 pr-8 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:border-[color:var(--mr-blue)]" />
          {q && <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1" data-testid="pipeline-clear"><X size={14} /></button>}
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
            <button onClick={() => setScope('me')} data-testid="scope-me"
              className={`px-2.5 py-1 text-[11px] font-bold rounded ${scope === 'me' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>My pipeline</button>
            <button onClick={() => setScope('team')} data-testid="scope-team"
              className={`px-2.5 py-1 text-[11px] font-bold rounded ${scope === 'team' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>Team</button>
          </div>
          <span className="text-[11px] text-muted-foreground ml-auto">Showing <span className="font-bold text-foreground">{data.shown}</span> of {data.total}</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`whitespace-nowrap text-[11px] font-bold px-2.5 py-1.5 rounded-full border flex-shrink-0 ${
                filter === f.id ? 'bg-[color:var(--mr-red)] text-white border-[color:var(--mr-red)]' : 'bg-card text-muted-foreground border-border'
              }`}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-1.5 pt-3">
        {loading ? (
          <>{[0,1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-2">{error}</p>
            <button onClick={load} className="mr-btn-primary px-4 py-2 rounded-lg text-sm">Retry</button>
          </div>
        ) : data.rows.length === 0 ? (
          <div className="text-center py-10" data-testid="pipeline-empty">
            <p className="text-sm font-bold text-foreground">No files match</p>
            <p className="text-xs text-muted-foreground mt-1">{q ? `No borrowers matching "${q}"` : 'Try a different filter.'}</p>
          </div>
        ) : (
          data.rows.map(row => <PipelineRow key={row.id} row={row} onOpen={() => handleOpen(row.id)} />)
        )}
      </div>

      {openFile && <MobileLoanDetail fileId={openFile} onClose={() => setOpenFile(null)} currentUser={user} />}
    </div>
  );
}

function PipelineRow({ row, onOpen }) {
  const urgencyCls = row.urgency === 'Critical' ? 'chip-critical' : row.urgency === 'High' ? 'chip-warn' : row.urgency === 'Normal' ? 'chip-neutral' : 'chip-info';
  let dueLabel = null, dueCls = 'text-muted-foreground';
  if (row.days_to_close !== null && row.days_to_close !== undefined) {
    if (row.days_to_close < 0) { dueLabel = `${Math.abs(row.days_to_close)}d overdue`; dueCls = 'text-[color:var(--mr-red-strong)] font-bold'; }
    else if (row.days_to_close === 0) { dueLabel = 'Closes today'; dueCls = 'text-[color:var(--mr-red-strong)] font-bold'; }
    else if (row.days_to_close <= 7) { dueLabel = `Closes in ${row.days_to_close}d`; dueCls = 'text-[color:var(--mr-warn)] font-bold'; }
    else { dueLabel = row.closing_date; }
  }
  return (
    <button onClick={onOpen} data-testid={`pipeline-row-${row.id}`}
      className="w-full text-left mr-card p-2.5 flex items-center gap-2 min-h-[56px] active:bg-muted/40">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-bold text-foreground truncate">{row.borrower}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${urgencyCls}`}>{row.urgency}</span>
          {row.has_escalation && <Warning size={11} weight="fill" className="text-[color:var(--mr-red)]" />}
          {row.has_flag && <Fire size={11} weight="fill" className="text-[color:var(--mr-warn)]" />}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className="text-[11px] text-muted-foreground">{row.status}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-[11px] text-foreground truncate">{row.action_item || '—'}</span>
          {dueLabel && (<><span className="text-muted-foreground">·</span><span className={`text-[11px] ${dueCls}`}>{dueLabel}</span></>)}
          {row.assigned_to && (<><span className="text-muted-foreground">·</span><span className="text-[11px] text-muted-foreground font-semibold">{row.assigned_to}</span></>)}
        </div>
      </div>
      <CaretRight size={14} className="text-muted-foreground flex-shrink-0" />
    </button>
  );
}
